import { apiService } from '@services/api.service.ts';
import {
  CompaniesResponse,
  CompanyProfile,
  UpdateCompanyArg
} from '@/types/company.interface.ts';

const companyService = apiService.injectEndpoints({
  endpoints: build => ({
    getCompanies: build.query<
      CompaniesResponse,
      { status: 'active' | 'pending'; page?: number; page_size?: number }
    >({
      query: ({ status, page = 1, page_size = 10 }) => ({
        url: `/dashboard/companies/`,
        params: { status, page, page_size },
        method: 'GET'
      }),
      providesTags: (_result, _error, { status, page, page_size }) => [
        { type: 'Companies', id: `${status}-${page}-${page_size}` }
      ],
      transformResponse: (
        response: CompaniesResponse,
        meta
      ): CompaniesResponse => {
        if (meta?.response?.ok) {
          return response;
        }
        return response;
      }
    }),

    getCompaniesId: build.query<
      CompanyProfile | null,
      { status: 'active' | 'pending'; id: string }
    >({
      query: ({ status, id }) => ({
        url: `/dashboard/companies/${id}`,
        params: { status },
        method: 'GET'
      }),
      providesTags: (_result, _error, { status }) => [
        { type: 'Companies', id: status }
      ],
      transformResponse: (
        response: { success: boolean; message: string; data: CompanyProfile },
        meta
      ): CompanyProfile | null => {
        if (meta?.response?.ok) {
          return response.data;
        }
        return null;
      }
    }),

    activateCompany: build.mutation<any, { activation_key: string }>({
      query: ({ activation_key }) => ({
        url: `/account/activate/${activation_key}/`,
        method: 'POST'
      }),
      invalidatesTags: [{ type: 'Companies' }],
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(
            apiService.util.invalidateTags([
              { type: 'Companies', id: 'pending' },
              { type: 'Companies', id: 'active' }
            ])
          );
        } catch {
          console.warn('Activation failed, not invalidating cache.');
        }
      },
      transformResponse: (response: CompaniesResponse, meta) => {
        if (meta?.response?.ok) {
          return response;
        }
        return response;
      }
    }),

    deleteCompany: build.mutation<
      any,
      { id: string; status: 'active' | 'pending' }
    >({
      query: ({ id, status }) => ({
        url: `/dashboard/companies/${id}/`,
        params: { status },
        method: 'DELETE'
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(
            apiService.util.invalidateTags([
              { type: 'Companies', id: 'pending' },
              { type: 'Companies', id: 'active' }
            ])
          );
        } catch {
          console.warn('Delete failed, not invalidating cache.');
        }
      },
      transformResponse: (response, meta) => {
        if (meta?.response?.ok) {
          return response;
        }
        return response;
      }
    }),

    updateCompany: build.mutation<any, UpdateCompanyArg>({
      query: ({ id, data }) => ({
        url: `/dashboard/companies/${id}/`,
        method: 'PUT',
        body: data,
        params: {
          status: 'pending'
        }
      }),
      async onQueryStarted(_arg, { queryFulfilled, dispatch }) {
        try {
          await queryFulfilled;
          dispatch(
            apiService.util.invalidateTags([
              { type: 'Companies', id: 'pending' },
              { type: 'Companies', id: 'active' }
            ])
          );
        } catch {
          console.warn('Update failed, not invalidating cache.');
        }
      },
      transformResponse: (response, meta) => {
        if (meta?.response?.ok) {
          return response;
        }
        return response;
      }
    }),

    downloadCompanies: build.mutation<
      {
        success: boolean;
        message: string;
        data: {
          filename: string;
          url: string;
          s3_key: string;
          total_records: number;
        };
      },
      { ids: string[]; export_format?: string }
    >({
      query: ({ ids, export_format = 'csv' }) => ({
        url: `/dashboard/companies/export-data/`,
        method: 'POST',
        body: { company_ids: ids, export_format }
      })
    })
  }),
  overrideExisting: false
});

export const {
  useGetCompaniesQuery,
  useGetCompaniesIdQuery,
  useActivateCompanyMutation,
  useDeleteCompanyMutation,
  useUpdateCompanyMutation,
  useDownloadCompaniesMutation
} = companyService;
